import React, { useRef, useState } from 'react';
import WheelFitmentCheck from '../WheelFitmentCheck';
import css from './ViewWheelsOnCar.module.css';

const MAX_FILE_BYTES = 8 * 1024 * 1024;
const RESIZE_THRESHOLD_BYTES = 6 * 1024 * 1024;
const MAX_IMAGE_DIMENSION = 2048;
const ACCEPTED_TYPES = ['image/png', 'image/jpeg', 'image/webp'];
const POLL_INTERVAL_MS = 2500;
const MAX_POLL_ATTEMPTS = 60;

const loadImage = (file) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = (e) => {
      URL.revokeObjectURL(url);
      reject(e);
    };
    img.src = url;
  });

const canvasToBlob = (canvas, type, quality) =>
  new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('Canvas encode failed'))),
      type,
      quality
    );
  });

// Downscale + recompress to keep upload under the 8MB WheelBlend limit.
// Returns the original File if it's already small enough.
const prepareImageForUpload = async (file) => {
  const withinDimension =
    file.size <= RESIZE_THRESHOLD_BYTES; // assume small files have reasonable dims
  if (withinDimension && file.size <= MAX_FILE_BYTES) return file;

  const img = await loadImage(file);
  const { naturalWidth: w, naturalHeight: h } = img;
  const scale = Math.min(1, MAX_IMAGE_DIMENSION / Math.max(w, h));
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(w * scale));
  canvas.height = Math.max(1, Math.round(h * scale));
  canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);

  for (const quality of [0.85, 0.7, 0.55]) {
    const blob = await canvasToBlob(canvas, 'image/jpeg', quality);
    if (blob.size <= MAX_FILE_BYTES) {
      return new File([blob], file.name.replace(/\.[^.]+$/, '') + '.jpg', {
        type: 'image/jpeg',
      });
    }
  }
  throw new Error('TOO_LARGE_AFTER_RESIZE');
};

const getListingWheelImageUrl = (listing) => {
  const images = listing?.images || [];
  for (const img of images) {
    const variants = img?.attributes?.variants || {};
    const url =
      variants['scaled-large']?.url ||
      variants['scaled-medium']?.url ||
      variants['default']?.url;
    if (url) return url;
  }
  return null;
};

const errorMessageFor = (status, code) => {
  if (status === 402 || code === 'INSUFFICIENT_BALANCE')
    return 'Tjänsten är tillfälligt otillgänglig (otillräckligt saldo). Försök igen senare.';
  if (code === 'RATE_LIMIT_REACHED' || status === 429)
    return 'För många försök just nu. Vänta en stund och försök igen.';
  if (code === 'CONCURRENCY_LIMIT_REACHED')
    return 'För många pågående renderingar. Försök igen om en stund.';
  if (code === 'ASSET_NOT_FOUND') return 'Bilden kunde inte läsas in. Försök ladda upp igen.';
  if (status === 401) return 'Tjänsten kunde inte autentisera. Kontakta supporten.';
  return 'Något gick fel. Försök igen.';
};

const ViewWheelsOnCar = ({ listing }) => {
  const [open, setOpen] = useState(false);
  // The listing entity briefly loses its images while the page refetches it,
  // which used to unmount this whole 294px panel and mount it again about
  // 200ms later. The content below jumped twice, and on desktop that single
  // flicker was worth 0.29 CLS - enough on its own to rate the listing page
  // "poor". Once an image has been seen, keep using it.
  const currentWheelImageUrl = getListingWheelImageUrl(listing);
  const lastWheelImageUrl = useRef(null);
  if (currentWheelImageUrl) {
    lastWheelImageUrl.current = currentWheelImageUrl;
  }
  const wheelImageUrl = currentWheelImageUrl || lastWheelImageUrl.current;

  const [carFile, setCarFile] = useState(null);
  const [carPreview, setCarPreview] = useState(null);
  const [status, setStatus] = useState('idle');
  const [resultUrl, setResultUrl] = useState(null);
  const [error, setError] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);
  const sessionNonce = useRef(Math.random().toString(36).slice(2, 10));

  const isBusy = status === 'uploading' || status === 'rendering';

  const handleFile = (file) => {
    if (!file) return;
    if (!ACCEPTED_TYPES.includes(file.type)) {
      setError('Bildformatet stöds inte. Använd PNG, JPEG eller WEBP.');
      return;
    }
    setError(null);
    setResultUrl(null);
    setCarFile(file);
    if (carPreview) URL.revokeObjectURL(carPreview);
    setCarPreview(URL.createObjectURL(file));
    setStatus('preview');
  };

  const onFileChange = (e) => handleFile(e.target.files?.[0]);

  const onDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    handleFile(e.dataTransfer.files?.[0]);
  };

  const signedUpload = async (blob, kind) => {
    const url = `/api/wheelblend/upload?kind=${encodeURIComponent(kind)}&contentType=${encodeURIComponent(blob.type)}`;
    const resp = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': blob.type },
      body: blob,
    });
    const data = await resp.json().catch(() => ({}));
    if (!resp.ok) {
      const err = new Error(data?.error || 'Upload failed');
      err.status = resp.status;
      err.code = data?.code;
      throw err;
    }
    return data.key;
  };

  const run = async () => {
    if (!carFile || !wheelImageUrl) return;
    setError(null);
    setResultUrl(null);

    try {
      setStatus('uploading');

      const wheelBlob = await fetch(wheelImageUrl).then((r) => {
        if (!r.ok) throw new Error('Could not load listing wheel image');
        return r.blob();
      });
      const wheelTypedBlob = wheelBlob.type && ACCEPTED_TYPES.includes(wheelBlob.type)
        ? wheelBlob
        : new Blob([wheelBlob], { type: 'image/jpeg' });

      const wheelKey = await signedUpload(wheelTypedBlob, 'wheel');

      let carUpload;
      try {
        carUpload = await prepareImageForUpload(carFile);
      } catch (e) {
        if (e.message === 'TOO_LARGE_AFTER_RESIZE') {
          setError('Bilden kunde inte komprimeras tillräckligt. Försök med en mindre bild.');
          setStatus('preview');
          return;
        }
        throw e;
      }
      const carKey = await signedUpload(carUpload, 'car');

      setStatus('rendering');

      const idempotencyKey = `${listing?.id?.uuid || 'listing'}-${carFile.name}-${carFile.size}-${sessionNonce.current}`;
      const createResp = await fetch('/api/wheelblend/renders', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ carKey, wheelKey, idempotencyKey }),
      });
      const job = await createResp.json();
      if (!createResp.ok) {
        const err = new Error(job?.error || 'Render create failed');
        err.status = createResp.status;
        err.code = job?.code;
        throw err;
      }

      // Create response: { renderId, state, resultUrl, ... } at top level
      // Poll response:   { status: 'success', render: { id, state, resultUrl, error, ... } }
      let state = job.state;
      let url = job.resultUrl;
      let attempts = 0;
      while ((state === 'PENDING' || state === 'PROCESSING') && attempts < MAX_POLL_ATTEMPTS) {
        await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
        attempts += 1;
        const pollResp = await fetch(`/api/wheelblend/renders/${job.renderId}`);
        const poll = await pollResp.json();
        if (!pollResp.ok) {
          const err = new Error(poll?.error || poll?.message || 'Render poll failed');
          err.status = pollResp.status;
          err.code = poll?.code;
          throw err;
        }
        const render = poll.render || poll;
        state = render.state;
        url = render.resultUrl;
        if (state === 'ERROR') {
          const err = new Error(render.error || 'Render failed');
          err.code = render.code;
          throw err;
        }
      }

      if (state === 'DONE' && url) {
        setResultUrl(url);
        setStatus('done');
      } else {
        throw new Error('Render timed out');
      }
    } catch (e) {
      setError(errorMessageFor(e.status, e.code));
      setStatus('preview');
    }
  };

  const reset = () => {
    if (carPreview) URL.revokeObjectURL(carPreview);
    setCarFile(null);
    setCarPreview(null);
    setResultUrl(null);
    setError(null);
    setStatus('idle');
  };

  if (!wheelImageUrl) return null;

  /* P25009-482 BR-9 / BR-11 — the preview is a nice-to-have below the
   * buying decision, so it starts collapsed: one row that says what it does.
   * Once the buyer has started (a photo chosen), it stays open. */
  const isOpen = open || status !== 'idle';

  return (
    <div className={css.root}>
      <button
        type="button"
        className={`${css.header} ${css.headerToggle} ${isOpen ? '' : css.headerClosed}`}
        onClick={() => setOpen(o => !o)}
        aria-expanded={isOpen}
        disabled={status !== 'idle'}
      >
        <div className={css.headerIcon}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
            <circle cx="12" cy="12" r="9" />
            <circle cx="12" cy="12" r="3" />
            <path d="M12 3v3M12 18v3M3 12h3M18 12h3" strokeLinecap="round" />
          </svg>
        </div>
        <div className={css.headerText}>
          <h3>Visa hjulen på din bil</h3>
          <p>Ladda upp en bild på din bil – AI genererar en fotorealistisk förhandsvisning</p>
        </div>
        <span className={css.headerChevron} aria-hidden="true">
          {isOpen ? '⌃' : '⌄'}
        </span>
      </button>

      {isOpen && status === 'idle' ? (
        <>
          <div
            className={`${css.dropzone} ${isDragging ? css.dropzoneDragging : ''}`}
            onClick={() => fileInputRef.current?.click()}
            onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
            onDragLeave={() => setIsDragging(false)}
            onDrop={onDrop}
            role="button"
            tabIndex={0}
          >
            <div className={css.dropzoneIconWrap}>
              <svg className={css.dropzoneIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6.827 6.175A2.31 2.31 0 015.186 7.23c-.38.054-.757.112-1.134.175C2.999 7.58 2.25 8.507 2.25 9.574V18a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18V9.574c0-1.067-.75-1.994-1.802-2.169a47.865 47.865 0 00-1.134-.175 2.31 2.31 0 01-1.64-1.055l-.822-1.316a2.192 2.192 0 00-1.736-1.039 48.774 48.774 0 00-5.232 0 2.192 2.192 0 00-1.736 1.039l-.821 1.316z" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 12.75a4.5 4.5 0 11-9 0 4.5 4.5 0 019 0z" />
              </svg>
            </div>
            <p className={css.dropzoneText}>Ta en bild eller ladda upp</p>
            <p className={css.dropzoneHint}>Tydlig bild från sidan ger bäst resultat. PNG, JPEG eller WEBP.</p>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/png,image/jpeg,image/webp"
            className={css.hiddenInput}
            onChange={onFileChange}
          />
          {error ? <p className={css.errorText}>{error}</p> : null}
        </>
      ) : null}

      {status === 'preview' ? (
        <>
          <div className={css.imageFrame}>
            {carPreview ? <img src={carPreview} alt="Uppladdad bil" /> : null}
          </div>
          {error ? <p className={css.errorText}>{error}</p> : null}
          <div className={css.actions}>
            <button type="button" className={css.primaryBtn} onClick={run} disabled={isBusy}>
              Generera bild
            </button>
            <button type="button" className={css.secondaryBtn} onClick={reset} disabled={isBusy}>
              Ny bild
            </button>
          </div>
        </>
      ) : null}

      {status === 'uploading' || status === 'rendering' ? (
        <div className={css.imageFrame}>
          <div className={css.loading}>
            <div className={`${css.loadingBlob} ${css.loadingBlob1}`} />
            <div className={`${css.loadingBlob} ${css.loadingBlob2}`} />
            <div className={`${css.loadingBlob} ${css.loadingBlob3}`} />
            <div className={css.shimmer} />
            <div className={css.loadingCenter}>
              <span className={css.loadingText}>
                {status === 'uploading' ? 'Laddar upp bilder…' : 'AI renderar…'}
              </span>
            </div>
          </div>
        </div>
      ) : null}

      {status === 'done' && resultUrl ? (
        <>
          <div className={css.comparison}>
            <div className={css.comparisonCell}>
              <p className={css.comparisonLabel}>Din bil</p>
              <div className={css.imageFrame}>
                <img src={carPreview} alt="Din bil" />
              </div>
            </div>
            <div className={css.comparisonCell}>
              <p className={css.comparisonLabel}>Med dessa hjul</p>
              <div className={css.imageFrame}>
                <img src={resultUrl} alt="Förhandsvisning med hjul" />
              </div>
            </div>
          </div>
          <div className={css.actions}>
            <button type="button" className={css.secondaryBtn} onClick={reset}>
              Prova en annan bild
            </button>
          </div>
        </>
      ) : null}

      {status === 'done' && resultUrl ? <WheelFitmentCheck listing={listing} /> : null}
    </div>
  );
};

export default ViewWheelsOnCar;
